// AnonymousSignupBanner, banner discreto rodapé que convida visitante anônimo
// a criar conta. Mensagem inspiradora, sem barreira; pode ser fechado e fica
// silenciado por 24h via localStorage.
//
// Regras conforme produto:
//   • Aparece NO FINAL da tela, sem bloquear conteúdo
//   • Tom convidativo ("salve sua jornada"), nunca punitivo
//   • Dismiss persistido em localStorage por 24h
//   • Não aparece para usuários autenticados
//   • Sprint refinamento — C: delay de 20s OU primeiro scroll significativo.
//     Aparece APENAS UMA VEZ POR SESSÃO (sessionStorage), evitando reaparecer
//     entre navegações internas.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { useAuth } from "../lib/authContext";

const LS_KEY = "cm_anon_banner_dismissed_until";
const SS_SHOWN_KEY = "cm_anon_banner_shown_this_session";
const READY_DELAY_MS = 20000; // 20s de leitura antes de aparecer
const SCROLL_GATE_PX = 320; // primeiro scroll significativo (~1 viewport mobile)

function isDismissed() {
  try {
    const v = Number(localStorage.getItem(LS_KEY) || 0);
    return v > Date.now();
  } catch {
    return false;
  }
}

function wasShownThisSession() {
  try {
    return sessionStorage.getItem(SS_SHOWN_KEY) === "1";
  } catch {
    return false;
  }
}

function markShownThisSession() {
  try {
    sessionStorage.setItem(SS_SHOWN_KEY, "1");
  } catch {
    /* ignore */
  }
}

function dismissFor24h() {
  try {
    const until = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem(LS_KEY, String(until));
  } catch {
    /* ignore */
  }
}

export default function AnonymousSignupBanner() {
  const { isAuthenticated, isChecking } = useAuth();
  const [hidden, setHidden] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isChecking || isAuthenticated) {
      setHidden(true);
      return;
    }
    // Já mostrado nesta sessão → silêncio permanente até fechar o navegador.
    if (wasShownThisSession() || isDismissed()) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  }, [isAuthenticated, isChecking]);

  // Delay + scroll gate — aparece só quando o usuário está engajado.
  useEffect(() => {
    if (isChecking || isAuthenticated) return;
    if (wasShownThisSession() || isDismissed()) return;
    let cancelled = false;

    const trigger = () => {
      if (cancelled) return;
      markShownThisSession(); // garante 1×/sessão
      setReady(true);
    };

    const timer = window.setTimeout(trigger, READY_DELAY_MS);

    const onScroll = () => {
      if (window.scrollY >= SCROLL_GATE_PX) {
        trigger();
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [isChecking, isAuthenticated]);

  if (isAuthenticated || hidden || !ready) return null;

  const close = () => {
    dismissFor24h();
    setHidden(true);
  };

  return (
    <div
      data-testid="anonymous-signup-banner"
      // Sprint refinamento — E: coreografia calma dos flutuantes.
      //  Em mobile: right-24 (96px) deixa >=16px de gap horizontal para o
      //  WhatsAppButton (right-4 + w-14 = 72px). bottom-28 sobe acima da
      //  bottom nav (~72px altura). Em desktop volta para right-3/bottom-6.
      //  z-40 fica abaixo do WhatsApp (z-50) e ambos acima da bottom nav.
      className="fixed left-3 right-24 sm:right-3 bottom-28 sm:bottom-6 z-40 pointer-events-none"
    >
      <div className="max-w-2xl mx-auto pointer-events-auto rounded-2xl bg-navy text-white shadow-lg border border-gold/40">
        <div className="p-4 sm:p-5 flex items-start gap-4">
          <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
              Sua caminhada
            </p>
            <p className="font-display text-base sm:text-lg leading-snug mt-1">
              Crie sua conta gratuita e salve sua jornada espiritual.
            </p>
            <p className="text-[12px] text-white/65 mt-1 leading-relaxed">
              Favoritos, plano semanal e histórico ficam guardados em qualquer
              dispositivo.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Link
                to="/cadastro"
                data-testid="anonymous-banner-signup"
                className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-white px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                onClick={() => dismissFor24h()}
              >
                Criar conta
              </Link>
              <Link
                to="/entrar"
                data-testid="anonymous-banner-login"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-[11px] uppercase tracking-widest border border-white/15 transition-colors"
                onClick={() => dismissFor24h()}
              >
                Entrar
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="anonymous-banner-close"
            aria-label="Fechar"
            className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center shrink-0"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
        </div>
      </div>
    </div>
  );
}
